<template>
  <div class="home-container">
    <header class="home-header">
      <div class="user-section">
        <div class="user-avatar">
          <img
            src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23667eea'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E"
            alt="用户头像"
            class="avatar-img"
          />
        </div>
        <div class="user-name">{{ userInfo }}</div>
        <button class="logout-btn" @click="handleLogout" title="退出登录">
          <svg class="logout-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
        </button>
      </div>
    </header>
    <div class="header-content">
      <div class="title">AI⁺智慧养老健康平台</div>
    </div>
    <main class="home-main">
      <div class="platform-section">
        <div class="app-grid">
          <div
            v-for="(app, key) in availableApps"
            :key="key"
            class="app-card"
            @click="enterSubApp(String(key))"
          >
            <div class="app-icon">
              <i :class="`icon-${app.icon}`"></i>
            </div>
            <div class="app-info">
              <h3>{{ app.title }}</h3>
              <p>{{ app.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, watchEffect, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { useRouter } from 'vue-router';
import { useMenuStore } from '../store/menu';
import { useUserStore } from '../store/user';
import { FRONTEND_MENUS, type SubApp } from '../constants/menus';
import { subAppDataService } from '../services/SubAppDataService';
import { ElLoading } from 'element-plus';
import type { LoadingInstance } from 'element-plus/es/components/loading/src/loading';
import { getTenantLogo } from '../api/auth';

const isDev = import.meta.env.MODE === 'development';
const router = useRouter();
const menuStore = useMenuStore();
const userStore = useUserStore();

// 监听用户状态变化，在检测到异常时重新初始化
watchEffect(() => {
  if (userStore.token && !userStore.userInfo && !userStore.isLoading) {
    setTimeout(() => {
      userStore.initializeAuth();
    }, 100);
  }
});

const userInfo = computed(() => {
  const user = userStore.userInfo;
  const hasToken = !!userStore.token;
  const isLoading = userStore.isLoading;

  if (isLoading) {
    return '加载中...';
  }

  if (hasToken && !user) {
    return '状态恢复中...';
  }

  if (!hasToken && !user) {
    return '请登录';
  }

  return user?.sysUser?.name || '未知用户';
});


// 获取可用的菜单
const availableApps = computed(() => {
  // 先隐藏之前的逻辑
  // const availableMenuNames = menuStore.availableMenus.map(menu => menu.name);
  // console.log('availableMenuNames----', availableMenuNames);

  // 获取过滤后的 FRONTEND_MENUS
  // const filteredMenus = Object.entries(FRONTEND_MENUS)
  //   .filter(([_, app]) => availableMenuNames.includes(app.title))
  //   .reduce((acc, [key, app]) => {
  //     acc[key] = app;
  //     return acc;
  //   }, {} as Record<string, (typeof FRONTEND_MENUS)[keyof typeof FRONTEND_MENUS]>);
  const availableMenus = menuStore.availableMenus;
  const mapMenus = availableMenus.reduce((acc, menu) => {
    const key = menu?.meta?.enName;
    if (key) {
      acc[key] = {
        name: key,
        title: menu.name,
        description: menu?.meta?.description,
        icon: menu?.meta?.icon,
        id: menu?.id
      };
    }
    return acc;
  }, {} as Record<string, any>);

  const jg = {
    name: 'educationCare',
    title: '机构智慧养老平台',
    description: '精准服务 精细管理长者',
    icon: 'community'
  };

  let result: Record<string, any> = { ...mapMenus };
  
  try {
    const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
    if (userInfo?.sysUser?.username?.startsWith('ys_')) {
      result = { ...mapMenus, jg };
    }
  } catch (error) {
    console.error('获取用户信息失败', error);
  }

  return result;
});

const enterSubApp = async (appName: string) => {
  // 检查是否是外部链接应用
  const appConfig = FRONTEND_MENUS[appName];
  if (appConfig?.isExternal) {
    // 打开新页签跳转到外部链接
    window.open(appConfig.entry, '_blank');
    return;
  }

  // 处理正常的子应用
  // 准备传递给子应用的数据
  const subAppData = {
    userId: userStore.userInfo?.sysUser?.userId || userStore.userInfo?.id || '',
    menuData: JSON.stringify(menuStore.menuList),
    authList: JSON.stringify(userStore.authList)
  };

  // 使用数据传递服务保存子应用数据
  subAppDataService.saveSubAppData(appName, subAppData);

  // 保存对应的name到localStorage
  const app = availableApps.value[appName];
  if (app) {
    localStorage.setItem(app.name, app.title);
    localStorage.setItem(`${app.name}_id`, app.id);
  }
  
  // 如果是用户管理应用，额外设置用户中心菜单数据
  if (appName === 'userManage') {
    const userCenterChildren = menuStore.getMenuChildren('统一用户中心');
    menuStore.setCurrentUserCenterMenu(userCenterChildren);
    subAppDataService.saveUserCenterData(appName, userCenterChildren);
  }
  if (appName === 'userManage') {
    router.push(`/sub-app/${appName}#/admin/user/index`);
  } else if (appName === 'jg') {
    localStorage.setItem('menu_type', 'jg');
    const baseUrl = window.location.origin;
    const targetUrl = `${baseUrl}/sub-app/educationCare`;
    window.open(targetUrl, '_blank');
  } else {
    localStorage.removeItem('menu_type');
    // 在新页签中打开子应用
    const baseUrl = window.location.origin;
    const targetUrl = `${baseUrl}/sub-app/${appName}`;
    window.open(targetUrl, '_blank');
  }
};

const handleLogout = () => {
  console.log('用户主动退出登录');

  // 清理所有子应用数据
  subAppDataService.clearAllSubAppData();

  // 清除用户状态和菜单状态
  userStore.clearAuth();
  menuStore.clearMenus();
  localStorage.clear();

  ElMessage.success({ message: '已退出登录', duration: 1000 });

  setTimeout(() => {
    router.replace('/login');
  }, 500);
};

const params = { type: 2, parentId: -1 };

onMounted(async () => {
  // 等待初始化完成
  await new Promise(resolve => setTimeout(resolve, 50));
  
  // 获取租户对应菜单Logo
  await getTenantLogoById(params);

  // 检查并确保用户状态正确恢复
  if (userStore.token && !userStore.userInfo && !userStore.isLoading) {
    userStore.initializeAuth();
    await new Promise(resolve => setTimeout(resolve, 200));
  }

  let loadingInstance: LoadingInstance | null = null;
  try {
    loadingInstance = ElLoading.service({ fullscreen: true, background: 'rgba(0, 0, 0, 0.8)' });
    await menuStore.getMenuList(params);

    watch(
      () => userStore.userInfo,
      newUserInfo => {
        if (newUserInfo) {
          if (Object.keys(availableApps.value).length === 1) {
            const appName = Object.keys(availableApps.value)[0];

            // 准备传递给子应用的数据
            const subAppData = {
              userId: userStore.userInfo?.sysUser?.userId || userStore.userInfo?.id || '',
              menuData: JSON.stringify(menuStore.menuList),
              authList: JSON.stringify(userStore.authList)
            };
            const app = availableApps.value[appName];
            if (app) {
              localStorage.setItem(app.name, app.title);
              localStorage.setItem(`${app.name}_id`, app.id);
            }
            // 使用数据传递服务保存子应用数据
            subAppDataService.saveSubAppData(appName, subAppData);

            // 如果是用户管理应用，额外设置用户中心菜单数据
            if (appName === 'userManage') {
              const userCenterChildren = menuStore.getMenuChildren('统一用户中心');
              menuStore.setCurrentUserCenterMenu(userCenterChildren);
              subAppDataService.saveUserCenterData(appName, userCenterChildren);
              router
                .push(`/sub-app/${appName}#/admin/user/index`)
                .then(() => loadingInstance?.close());
            } else {
              router.push(`/sub-app/${appName}`).then(() => loadingInstance?.close());
            }
          } else {
            loadingInstance?.close();
          }
        }
      },
      { immediate: true }
    );
  } catch (error: unknown) {
    loadingInstance?.close();
    if (error && typeof error === 'object' && 'name' in error && error.name !== 'CanceledError') {
      ElMessage.error('获取主应用菜单失败');
    }
  }
});

// 获取租户对应菜单Logo
const getTenantLogoById = async (params: any) => {
  // 获取store中的租户ID
  const tenantId = userStore.userInfo?.sysUser?.tenantId || '';
  params.id = tenantId;
  const res = await getTenantLogo(params);
  if (res.code === 0) {
    localStorage.setItem('tenantLogo', (res.data as any)?.tenantLogo || '');
    localStorage.setItem('tenantName', (res.data as any)?.tenantName || '');
  }
};

</script>

<style scoped>
/* 重置样式 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.home-container {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: url('@/assets/homeBg.png') no-repeat center/cover;
  display: flex;
  flex-direction: column;
}

.home-header {
  flex: 0 0 auto;
  padding: 20px 30px;
  /* background: rgba(255, 255, 255, 0.1); */
  /* backdrop-filter: blur(10px); */
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.header-content {
  text-align: left;
  padding-left: 100px;
}

.title {
  font-size: clamp(28px, 3.5vw, 60px);
  color: #2986dc;
  font-weight: bold;
}

.subtitle {
  font-size: clamp(12px, 1.8vw, 16px);
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-weight: 300;
}

.user-section {
  display: flex;
  align-items: center;
  gap: 15px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.3s ease;
}

.user-name {
  color: #2986dc;
  font-size: 16px;
  font-weight: 500;
}

.user-avatar:hover {
  background: rgba(255, 255, 255, 0.3);
}

.avatar-img {
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.logout-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background 0.3s ease;
  color: white;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.logout-icon {
  width: 20px;
  height: 20px;
  stroke: white;
}

.home-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  padding-top: 0;
  height: calc(100vh - 160px);
}

.platform-section {
  width: 100%;
  height: calc(100% - 60px);
  max-width: 1600px;
  margin: 20px 0;
}

.section-title {
  font-size: clamp(20px, 3vw, 28px);
  color: white;
  margin-bottom: 30px;
  text-align: center;
  position: relative;
  font-weight: 600;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #3498db, #2ecc71);
  border-radius: 2px;
}

.app-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  padding: 0 20px;
  align-content: center;
  height: 100%;
  max-width: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
}

.app-card {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  padding: 30px; /* 调整内边距 */
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
  text-align: center;
  backdrop-filter: blur(10px);
  border: 2px solid #ffffff;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
}

.app-card:hover {
  transform: translateY(-12px) scale(1.03);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.7);
}

.app-icon {
  width: 80px;
  height: 80px;
  margin: 10px auto 10px;
  /* flex-shrink: 0;  */
}

.app-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex-grow: 1;
  min-height: 0; /* 防止内容溢出 */
  padding: 5px 0;
  box-sizing: border-box;
  margin: 10px;
}

.app-info h3 {
  font-size: clamp(16px, 2vw, 22px);
  color: #333333;
  margin: 5px 0;
  font-weight: 600;
  word-break: break-word; /* 确保文本换行 */
  max-height: 40%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.app-info p {
  font-size: clamp(12px, 1.5vw, 14px);
  color: #8c95a4;
  margin: 5px 0;
  line-height: 1.3;
  word-break: break-word; /* 确保文本换行 */
  text-align: center;
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 40%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

/* 响应式布局 */
@media (max-width: 1200px) {
  .app-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  .app-card {
    padding: 20px;
  }

  .app-icon {
    width: 50px;
    height: 50px;
  }

  .app-info h3 {
    font-size: clamp(11px, 1.7vw, 15px);
  }

  .app-info p {
    font-size: clamp(9px, 1.1vw, 12px);
  }
}

@media (max-width: 768px) {
  .app-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  .app-card {
    padding: 15px;
  }

  .app-icon {
    width: 45px;
    height: 45px;
    margin: 5px auto 5px;
  }

  .app-info h3 {
    font-size: clamp(10px, 1.5vw, 14px);
    margin: 3px 0;
  }

  .app-info p {
    font-size: clamp(8px, 1vw, 11px);
    margin: 3px 0;
    line-height: 1.2;
  }
}

@media (max-width: 480px) {
  .app-grid {
    grid-template-columns: 1fr;
    gap: 15px;
    padding: 0 10px;
  }

  .app-card {
    padding: 20px;
    aspect-ratio: 1; /* 在小屏幕上保持正方形 */
  }

  .app-icon {
    width: 50px;
    height: 50px;
  }

  .app-info h3 {
    font-size: 14px;
  }

  .app-info p {
    font-size: 11px;
  }
}

/* 图标字体 */
.icon-users,
.icon-home,
.icon-institution,
.icon-community,
.icon-building,
.icon-statistics {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
}

.icon-users::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('../assets/userCenter.png') no-repeat center/contain;
}

.icon-home::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('../assets/homeCare.png') no-repeat center/contain;
}

.icon-institution::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('../assets/institutionCare.png') no-repeat center/contain;
}

.icon-community::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('../assets/communityCare.png') no-repeat center/contain;
}
.icon-statistics::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('../assets/statistics.png') no-repeat center/contain;
}

.icon-building {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
}

.icon-building::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('../assets/officerCare.png') no-repeat center/contain;
}
.icon-devices::before {
  content: '📱';
}
.version {
  font-size: 24px;
}
</style>
